// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { CsvImportResultsStep } from '@/csv-import/components/csv-import-results-step';
import { CsvImportStepper } from '@/csv-import/components/csv-import-stepper';
import { CsvImportTemplateStep } from '@/csv-import/components/csv-import-template-step';
import { CsvImportUploadStep } from '@/csv-import/components/csv-import-upload-step';
import type { CsvImportEnvelope } from '@/csv-import/schemas';
import type {
  CsvImportColumnSelection,
  CsvImportResultsView,
  CsvImportStep,
  CsvImportTemplateColumn,
} from '@/csv-import/types';
import { useSupportError } from '@/hooks/use-support-error';
import { Box, CardContent } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

interface CsvImportFlowProps<TResult extends CsvImportEnvelope> {
  templateStep: {
    title: string;
    columns: CsvImportTemplateColumn[];
    columnsHint?: string;
    infoAlert?: ReactNode;
    fileNamePrefix: string;
  };
  uploadStep: {
    dropZoneTitle: string;
    recordNoun: { singular: string; plural: string };
    warningAlert?: ReactNode;
  };
  uploadFile: (formData: FormData) => Promise<TResult>;
  /** Parses a 400 response body back into the upload result, or null when it is not one */
  parseErrorResponse: (data: unknown) => TResult | null;
  supportErrorMessage: string;
  buildResultsView: (result: TResult) => CsvImportResultsView;
  onSuccess?: () => void;
  onClose: () => void;
}

export function CsvImportFlow<TResult extends CsvImportEnvelope>(
  props: CsvImportFlowProps<TResult>
) {
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] =
    useState<CsvImportStep>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<TResult | null>(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [hasFatalError, setHasFatalError] = useState(false);
  const [selectedColumns, setSelectedColumns] =
    useState<CsvImportColumnSelection>({});

  const fileInputRef = useRef<HTMLInputElement>(null);
  const readingFileRef = useRef<File | null>(null);

  const clearFile = useCallback(() => {
    setFile(null);
    setUploadLineCount(undefined);
    readingFileRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSelectFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleToggleColumn = useCallback((header: string) => {
    setSelectedColumns((previous) => ({
      ...previous,
      [header]: !(previous[header] ?? true),
    }));
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);
        readingFileRef.current = selectedFile;

        const reader = new FileReader();
        reader.onload = (e) => {
          // A slower read of an earlier selection must not overwrite the count
          if (readingFileRef.current !== selectedFile) {
            return;
          }
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const { onSuccess } = props;
  const handleUploadResult = useCallback(
    (result: TResult) => {
      clearFile();
      setUploadResult(result);
      setUploadErrorMessage(null);
      setCurrentStep('results');
      if (result.success) {
        onSuccess?.();
      }
    },
    [clearFile, onSuccess]
  );

  const { parseErrorResponse, supportErrorMessage, uploadFile } = props;
  const { mutate: uploadCsv, isPending: isUploading } = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Missing file selection');
      }
      const formData = new FormData();
      formData.append('data', file);
      return uploadFile(formData);
    },
    onSuccess: (result) => {
      handleUploadResult(result);
    },
    onError: (error: AxiosError) => {
      clearFile();

      const knownResult = parseErrorResponse(error.response?.data);
      if (knownResult) {
        const hasContent =
          knownResult.items.length > 0 ||
          knownResult.structureErrors.length > 0 ||
          knownResult.rowErrors.length > 0;
        if (hasContent) {
          handleUploadResult(knownResult);
          return;
        }
      }

      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }

      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError(supportErrorMessage);
    },
  });

  const handleUpload = useCallback(() => {
    if (file) {
      setUploadErrorMessage(null);
      uploadCsv();
    }
  }, [file, uploadCsv]);

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    clearFile();
    setUploadResult(null);
    setUploadErrorMessage(null);
    setHasFatalError(false);
  }, [clearFile]);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
      setUploadErrorMessage(null);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const { buildResultsView } = props;
  const resultsView = useMemo(
    () => (uploadResult ? buildResultsView(uploadResult) : null),
    [buildResultsView, uploadResult]
  );

  return (
    <Box>
      <CardContent>
        <CsvImportStepper currentStep={currentStep} />

        {currentStep === 'download-template' && (
          <CsvImportTemplateStep
            title={props.templateStep.title}
            columns={props.templateStep.columns}
            columnsHint={props.templateStep.columnsHint}
            selectedColumns={selectedColumns}
            onToggleColumn={handleToggleColumn}
            infoAlert={props.templateStep.infoAlert}
            fileNamePrefix={props.templateStep.fileNamePrefix}
            onBack={props.onClose}
            onNext={handleNext}
          />
        )}

        {currentStep === 'upload-csv' && (
          <CsvImportUploadStep
            dropZoneTitle={props.uploadStep.dropZoneTitle}
            recordNoun={props.uploadStep.recordNoun}
            warningAlert={props.uploadStep.warningAlert}
            uploadErrorMessage={uploadErrorMessage}
            file={file}
            isUploading={isUploading}
            onSelectFile={handleSelectFile}
            onUpload={handleUpload}
            onBack={handleBack}
            onClose={props.onClose}
            hasFatalError={hasFatalError}
            uploadLineCount={uploadLineCount}
          />
        )}

        {currentStep === 'results' && (
          <CsvImportResultsStep
            view={resultsView}
            onBack={handleBack}
            onStartNew={handleStartNew}
            onClose={props.onClose}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </CardContent>
    </Box>
  );
}
